/**
 * AdminTasks Component
 * Admin interface for managing tasks
 */

import { useState, useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { api } from '../../services/api';

interface Task {
  id: string;
  title: string;
  description: string;
  url: string;
  reward: number;
  icon: string;
  isActive: boolean;
  completionCount: number;
  createdAt: string;
}

interface TaskFormData {
  title: string;
  description: string;
  url: string;
  reward: number;
  icon: string;
  imageFile?: File;
}

export function AdminTasks() {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [formData, setFormData] = useState<TaskFormData>({
    title: '',
    description: '',
    url: '',
    reward: 100,
    icon: '📋',
  });

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await api.getAdminTasks();
      setTasks(response.data.tasks);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({ ...formData, imageFile: file });
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Сначала загружаем изображение если есть
      let logoUrl = formData.icon;
      if (formData.imageFile) {
        try {
          const uploadResult = await api.uploadImage(formData.imageFile);
          logoUrl = uploadResult.url;
        } catch (error) {
          console.error('Failed to upload image:', error);
          alert('Failed to upload image');
          return;
        }
      }

      // Затем создаём задачу с URL изображения
      const taskData = {
        title: formData.title,
        description: formData.description,
        url: formData.url,
        reward: formData.reward,
        icon: logoUrl,
      };

      if (editingTask) {
        await api.updateTask(editingTask.id, taskData);
      } else {
        await api.createTask(taskData);
      }
      
      setShowCreateForm(false);
      setEditingTask(null);
      setFormData({
        title: '',
        description: '',
        url: '',
        reward: 100,
        icon: '📋',
      });
      setImagePreview(null);
      fetchTasks();
    } catch (error) {
      console.error('Failed to save task:', error);
      alert('Failed to save task');
    }
  };

  const handleEdit = (task: Task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description,
      url: task.url,
      reward: task.reward,
      icon: task.icon,
    });
    setShowCreateForm(true);
  };

  const handleToggleActive = async (taskId: string, isActive: boolean) => {
    try {
      await api.updateTask(taskId, { isActive: !isActive });
      fetchTasks();
    } catch (error) {
      console.error('Failed to toggle task status:', error);
    }
  };

  const handleDelete = async (taskId: string) => {
    if (!confirm(t('admin.confirm_delete'))) return;
    
    try {
      await api.deleteTask(taskId);
      fetchTasks();
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };



  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
      }}>
        <h2 style={{ margin: 0, color: '#fff' }}>{t('admin.tasks')}</h2>
        <button
          onClick={() => {
            setShowCreateForm(true);
            setEditingTask(null);
            setFormData({
              title: '',
              description: '',
              url: '',
              reward: 100,
              icon: '📋',
            });
            setImagePreview(null);
          }}
          style={{
            padding: '8px 16px',
            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
          }}
        >
          {t('admin.create_task')}
        </button>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div style={{
          background: 'rgba(10, 22, 40, 0.8)',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '20px',
          border: '1px solid rgba(59, 130, 246, 0.3)',
        }}>
          <h3 style={{ color: '#fff', marginBottom: '16px' }}>
            {editingTask ? t('admin.edit_task') : t('admin.create_task')}
          </h3>
          
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#fff', marginBottom: '8px' }}>
                {t('admin.task_title')}
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px',
                  color: '#fff',
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#fff', marginBottom: '8px' }}>
                {t('admin.task_description')}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px',
                  color: '#fff',
                  resize: 'vertical',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', color: '#fff', marginBottom: '8px' }}>
                  {t('admin.task_reward')}
                </label>
                <input
                  type="number"
                  value={formData.reward}
                  onChange={(e) => setFormData({ ...formData, reward: Number(e.target.value) })}
                  required
                  min="1"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '6px',
                    color: '#fff',
                  }}
                />
              </div>

              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', color: '#fff', marginBottom: '8px' }}>
                  Лого (изображение или эмодзи)
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '6px',
                      color: '#fff',
                    }}
                  />
                  {imagePreview && (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: '60px',
                        height: '60px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                      }}
                    />
                  )}
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    placeholder="📋 🎮 📺 🌐 (или загрузите изображение)"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '6px',
                      color: '#fff',
                      fontSize: '16px',
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: '#fff', marginBottom: '8px' }}>
                {t('admin.task_url')}
              </label>
              <input
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                required
                placeholder="https://..."
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px',
                  color: '#fff',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="submit"
                style={{
                  padding: '8px 16px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                {editingTask ? t('common.save') : t('admin.create_task')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingTask(null);
                  setImagePreview(null);
                }}
                style={{
                  padding: '8px 16px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: '#fff',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tasks List */}
      <div style={{ display: 'grid', gap: '12px' }}>
        {tasks.map((task) => (
          <div
            key={task.id}
            style={{
              background: 'rgba(10, 22, 40, 0.6)',
              padding: '16px',
              borderRadius: '12px',
              border: '1px solid rgba(59, 130, 246, 0.2)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div style={{
                fontSize: '32px',
                flexShrink: 0,
              }}>
                {task.icon?.startsWith('/uploads/') ? (
                  <img 
                    src={`https://wintrade.cfd${task.icon}`}
                    alt="Task icon"
                    style={{
                      width: '32px',
                      height: '32px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                    }}
                  />
                ) : (
                  task.icon
                )}
              </div>

              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <h3 style={{ color: '#fff', margin: 0, fontSize: '18px' }}>
                    {task.title}
                  </h3>
                  <span style={{
                    padding: '2px 8px',
                    background: task.isActive ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                    color: task.isActive ? '#22c55e' : '#ef4444',
                    borderRadius: '12px',
                    fontSize: '12px',
                  }}>
                    {task.isActive ? t('admin.active') : t('admin.inactive')}
                  </span>
                </div>

                <p style={{ color: 'rgba(255, 255, 255, 0.7)', margin: '0 0 12px', fontSize: '14px' }}>
                  {task.description}
                </p>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
                  <span style={{ color: '#fbbf24', fontWeight: 600 }}>
                    +{task.reward} WiTon
                  </span>
                  <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
                    {task.completionCount} {t('admin.completions')}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleEdit(task)}
                    style={{
                      padding: '6px 12px',
                      background: 'rgba(59, 130, 246, 0.2)',
                      color: '#3b82f6',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    onClick={() => handleToggleActive(task.id, task.isActive)}
                    style={{
                      padding: '6px 12px',
                      background: task.isActive ? 'rgba(239, 68, 68, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                      color: task.isActive ? '#ef4444' : '#22c55e',
                      border: `1px solid ${task.isActive ? 'rgba(239, 68, 68, 0.3)' : 'rgba(34, 197, 94, 0.3)'}`,
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    {task.isActive ? t('admin.deactivate') : t('admin.activate')}
                  </button>
                  <button
                    onClick={() => handleDelete(task.id)}
                    style={{
                      padding: '6px 12px',
                      background: 'rgba(239, 68, 68, 0.2)',
                      color: '#ef4444',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {tasks.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: 'rgba(255, 255, 255, 0.6)',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
          <div>{t('admin.no_tasks')}</div>
        </div>
      )}
    </div>
  );
}